import { useEffect, useState, type FormEvent } from 'react';
import {
  Sparkles,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
} from 'lucide-react';
import { apiFetch, isApiError } from '../lib/api';
import {
  createCommand,
  deleteCommand as apiDeleteCommand,
  updateCommand,
  type SiteCommand,
} from '../lib/commands-api';
import {
  Button,
  Badge,
  Input,
  Textarea,
  Label,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui';
import {
  describeActions,
  PLACEHOLDERS,
  type MacroActions,
  type MacroPriority,
  type MacroStatus,
} from '../lib/macro';
import styles from './Commands.module.css';
import { confirmDialog } from '../stores/confirm';

interface SiteLite {
  id: string;
  name: string;
  slug: string;
}

// Top-level manager for a site's slash-command snippets. Every agent assigned
// to the site may author/maintain them (they're a shared team resource), so
// this lives outside the super-admin console. Site picker is fed by
// /api/admin/sites, which returns only the sites the caller belongs to.
export function Commands() {
  const [sites, setSites] = useState<SiteLite[]>([]);
  const [siteId, setSiteId] = useState('');
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [sitesError, setSitesError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ sites: SiteLite[] }>('/api/admin/sites')
      .then((r) => {
        setSites(r.sites);
        setSiteId((prev) => prev || r.sites[0]?.id || '');
      })
      .catch(() => setSitesError('Couldn’t load your sites.'))
      .finally(() => setSitesLoaded(true));
  }, []);

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <div className="flex items-center gap-2 mb-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
            <span>Workflow Automation · Canned Responses</span>
          </div>
          <h1 className={styles.title}>Canned replies for agents</h1>
        </div>
        {sites.length > 1 && (
          <label className={styles.control}>
            <span className={styles.controlLabel}>Site</span>
            <select
              className={styles.select}
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      <p className={styles.intro}>
        Agents type <code className={styles.code}>/shortcut</code> in the chat composer to insert
        these replies. <strong>Team</strong> replies are shared with, and editable by, everyone on
        this site; <strong>personal</strong> ones only you see. Placeholders are filled in when the
        reply is inserted:{' '}
        {PLACEHOLDERS.map((p, i) => (
          <span key={p}>
            {i > 0 ? ', ' : ''}
            <code className={styles.code}>{p}</code>
          </span>
        ))}
        {' '}— add a fallback with <code className={styles.code}>{'{{visitor.first_name|there}}'}</code>.
        A reply can also set the conversation’s status, priority or tags when it is sent.
      </p>

      {sitesError && <p className={styles.error} role="alert">{sitesError}</p>}
      {sitesLoaded && !sitesError && sites.length === 0 && (
        <p className={styles.empty}>You’re not assigned to any sites yet.</p>
      )}

      {siteId && <CommandsManager siteId={siteId} />}
    </section>
  );
}

function CommandsManager({ siteId }: { siteId: string }) {
  const [commands, setCommands] = useState<SiteCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState({ shortcut: '', label: '', body: '' });
  const [personal, setPersonal] = useState(false);
  const [actions, setActions] = useState<ActionsDraft>(EMPTY_ACTIONS);

  async function load() {
    setLoading(true);
    try {
      const { commands } = await apiFetch<{ commands: SiteCommand[] }>(
        `/api/sites/${siteId}/commands`,
      );
      setCommands(commands);
      setError(null);
    } catch {
      setError('Could not load commands.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const shortcut = draft.shortcut.trim();
    const label = draft.label.trim();
    const body = draft.body;
    if (!shortcut || !label || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createCommand(siteId, {
        shortcut,
        label,
        body,
        sortOrder: commands.length,
        personal,
        actions: actionsFromDraft(actions),
      });
      setDraft({ shortcut: '', label: '', body: '' });
      setPersonal(false);
      setActions(EMPTY_ACTIONS);
      setModalOpen(false);
      await load();
    } catch (err) {
      if (isApiError(err) && (err.body as { error?: string })?.error === 'shortcut-taken') {
        setError(
          personal
            ? `You already have a personal command “/${shortcut}”.`
            : `A team command with the shortcut “/${shortcut}” already exists.`,
        );
      } else if (isApiError(err) && err.status === 400) {
        setError('Shortcut must start with a letter and use only letters, digits, _ or -.');
      } else {
        setError('Could not create command.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function save(id: string, patch: CommandPatch) {
    setError(null);
    try {
      await updateCommand(siteId, id, patch);
      await load();
    } catch (err) {
      if (isApiError(err) && (err.body as { error?: string })?.error === 'shortcut-taken') {
        setError('That shortcut is already taken on this site.');
      } else {
        setError('Save failed.');
      }
    }
  }

  async function remove(id: string) {
    if (!(await confirmDialog({ title: 'Delete this command?' }))) return;
    setError(null);
    try {
      await apiDeleteCommand(siteId, id);
      await load();
    } catch {
      setError('Delete failed.');
    }
  }

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-2">
        <span className="text-xs text-muted-foreground font-medium">
          {commands.length} {commands.length === 1 ? 'shortcut' : 'shortcuts'} configured for this site
        </span>
        <Button onClick={() => setModalOpen(true)} className="gap-1.5 h-9">
          <Plus size={15} />
          <span>New Slash Command</span>
        </Button>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Canned Slash Command</DialogTitle>
            <DialogDescription>
              Create a quick snippet for agents to insert by typing /shortcut in conversation.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4 pt-2" onSubmit={handleCreate}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cmd-shortcut" className="text-xs">Shortcut trigger</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">/</span>
                  <Input
                    id="cmd-shortcut"
                    className="pl-6 h-9 text-xs font-mono"
                    value={draft.shortcut}
                    onChange={(e) => setDraft((d) => ({ ...d, shortcut: e.target.value }))}
                    placeholder="greet"
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cmd-label" className="text-xs">Label / Menu title</Label>
                <Input
                  id="cmd-label"
                  className="h-9 text-xs"
                  value={draft.label}
                  onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                  placeholder="Friendly welcome"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cmd-body" className="text-xs">Snippet Body Text</Label>
              <Textarea
                id="cmd-body"
                className="min-h-[100px] text-xs"
                value={draft.body}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                placeholder="Hi {{visitor.first_name}}, this is {{agent.first_name}} — how can I help today?"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cmd-scope" className="text-xs">Visible to</Label>
              <select
                id="cmd-scope"
                className={styles.select}
                style={{ width: '100%', height: 36, fontSize: 13 }}
                value={personal ? 'personal' : 'team'}
                onChange={(e) => setPersonal(e.target.value === 'personal')}
              >
                <option value="team">Everyone on this site (team)</option>
                <option value="personal">Only me (personal)</option>
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-border">
              <ActionsFields value={actions} onChange={setActions} />
            </div>
            {error && <p className="text-xs text-destructive font-medium">{error}</p>}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy}>
                {busy ? 'Adding…' : 'Add Command'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {error && !modalOpen && <p className={styles.error} role="alert">{error}</p>}

      <div className={styles.tableCard}>
        <div className={styles.tableScroll}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Shortcut</TableHead>
                <TableHead className="w-[160px]">Label</TableHead>
                <TableHead>Snippet Content</TableHead>
                <TableHead className="w-[100px]">Visible to</TableHead>
                <TableHead className="w-[140px]">When sent</TableHead>
                <TableHead className="w-[60px] text-right">Order</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-64" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-16 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : commands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-3">
                        <Sparkles size={22} />
                      </div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">No canned snippets yet</h4>
                      <p className="text-xs text-muted-foreground max-w-xs mb-3">
                        Create shortcuts like /welcome or /pricing to empower agents with instant replies.
                      </p>
                      <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>
                        <Plus size={14} className="mr-1" /> Add First Command
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                commands.map((c) => (
                  <CommandRow
                    key={c.id}
                    command={c}
                    onSave={(patch) => save(c.id, patch)}
                    onDelete={() => remove(c.id)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}

function CommandRow({
  command,
  onSave,
  onDelete,
}: {
  command: SiteCommand;
  onSave: (patch: CommandPatch) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [shortcut, setShortcut] = useState(command.shortcut);
  const [label, setLabel] = useState(command.label);
  const [body, setBody] = useState(command.body);
  const [sortOrder, setSortOrder] = useState(command.sortOrder);
  const [actions, setActions] = useState<ActionsDraft>(() => draftFromActions(command.actions));

  function reset() {
    setShortcut(command.shortcut);
    setLabel(command.label);
    setBody(command.body);
    setSortOrder(command.sortOrder);
    setActions(draftFromActions(command.actions));
  }

  async function save() {
    const patch: CommandPatch = {};
    if (shortcut !== command.shortcut) patch.shortcut = shortcut;
    if (label !== command.label) patch.label = label;
    if (body !== command.body) patch.body = body;
    if (sortOrder !== command.sortOrder) patch.sortOrder = sortOrder;
    const nextActions = actionsFromDraft(actions);
    if (describeActions(nextActions) !== describeActions(command.actions)) patch.actions = nextActions;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    await onSave(patch);
    setEditing(false);
  }

  if (!editing) {
    return (
      <TableRow className="hover:bg-muted/40 transition-colors">
        <TableCell>
          <Badge variant="outline" className="font-mono text-xs font-semibold text-primary bg-primary/10 border-primary/25">
            /{command.shortcut}
          </Badge>
        </TableCell>
        <TableCell className="font-medium text-xs text-foreground">{command.label}</TableCell>
        <TableCell className="max-w-[320px]">
          <div className="truncate text-xs text-muted-foreground font-sans" title={command.body}>
            {command.body}
          </div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {command.ownerAgentId ? 'Only me' : 'Team'}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {describeActions(command.actions) || '—'}
        </TableCell>
        <TableCell className="text-right font-mono text-xs text-muted-foreground">{command.sortOrder}</TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs px-2 gap-1 text-muted-foreground hover:text-foreground"
              onClick={() => setEditing(true)}
              title="Edit command"
            >
              <Edit2 size={12} />
              <span>Edit</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs px-2 text-destructive hover:bg-destructive/10"
              onClick={() => void onDelete()}
              title="Delete command"
            >
              <Trash2 size={12} />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow className="bg-muted/30">
      <TableCell>
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">/</span>
          <Input
            className="pl-5 h-8 text-xs font-mono w-28"
            value={shortcut}
            onChange={(e) => setShortcut(e.target.value)}
          />
        </div>
      </TableCell>
      <TableCell>
        <Input
          className="h-8 text-xs w-36"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </TableCell>
      <TableCell>
        <Textarea
          className="min-h-[56px] text-xs py-1.5"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {command.ownerAgentId ? 'Only me' : 'Team'}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1.5 min-w-[200px]">
          <ActionsFields value={actions} onChange={setActions} />
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Input
          type="number"
          min={0}
          max={10000}
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
          className="h-8 text-xs font-mono w-16 ml-auto text-right"
        />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" className="h-7 text-xs px-2.5" onClick={() => void save()}>
            <Check size={12} className="mr-1" /> Save
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => {
              reset();
              setEditing(false);
            }}
          >
            <X size={12} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

type CommandPatch = Partial<Pick<SiteCommand, 'shortcut' | 'label' | 'body' | 'sortOrder' | 'actions'>>;

/** Form state for a command's actions; tags as typed (comma-separated). */
interface ActionsDraft {
  status: MacroStatus | '';
  priority: MacroPriority | '';
  tags: string;
}

const EMPTY_ACTIONS: ActionsDraft = { status: '', priority: '', tags: '' };

function draftFromActions(a: MacroActions): ActionsDraft {
  return { status: a.status ?? '', priority: a.priority ?? '', tags: (a.addTags ?? []).join(', ') };
}

function actionsFromDraft(d: ActionsDraft): MacroActions {
  const addTags = d.tags
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  return {
    ...(d.status ? { status: d.status } : {}),
    ...(d.priority ? { priority: d.priority } : {}),
    ...(addTags.length ? { addTags } : {}),
  };
}

/** "When sent" controls: the inbox changes a reply applies as it goes out. */
function ActionsFields({ value, onChange }: { value: ActionsDraft; onChange: (next: ActionsDraft) => void }) {
  return (
    <>
      <label className={styles.field}>
        <span className={styles.label}>When sent: status</span>
        <select
          className={styles.input}
          value={value.status}
          onChange={(e) => onChange({ ...value, status: e.target.value as ActionsDraft['status'] })}
        >
          <option value="">Leave as is</option>
          <option value="open">Open</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </label>
      <label className={styles.field}>
        <span className={styles.label}>When sent: priority</span>
        <select
          className={styles.input}
          value={value.priority}
          onChange={(e) => onChange({ ...value, priority: e.target.value as ActionsDraft['priority'] })}
        >
          <option value="">Leave as is</option>
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </label>
      <label className={styles.field}>
        <span className={styles.label}>When sent: add tags</span>
        <input
          className={styles.input}
          value={value.tags}
          onChange={(e) => onChange({ ...value, tags: e.target.value })}
          placeholder="billing, refund"
        />
      </label>
    </>
  );
}
